import React, { forwardRef } from "react";

export const InputField = forwardRef(
  (
    {
      label,
      name,
      type = "text",
      value,
      onChange,
      required = false,
      placeholder,
      disabled,
      options,
      defaultOptionLabel = "Select...",
      error,
      labelColor = "text-slate-900",
      accept,
      rows = 4,
      style,
      startIcon,
      endIcon,
      labelClassName = "",
      className = "",
      inputClassName = "", // Destructure inputClassName
      overrideInputClassName = false,
      ...props
    },
    ref
  ) => {
    const isCheckbox = type === "checkbox";
    const hideLabel = labelClassName.includes("sr-only");
    const selectBaseClass =
      "input w-full h-10 py-1 px-3 border border-gray-300 rounded-xl focus:ring-0 bg-white text-gray-900 text-sm font-medium disabled:bg-gray-100 disabled:text-gray-500";
    const fileBaseClass =
      "file-input file-input-bordered file-input-sm w-full h-10 py-1 pl-1.5 border border-gray-300 rounded-xl focus:ring-0 bg-white text-gray-900 text-sm font-medium disabled:bg-gray-100 disabled:text-gray-500 file:mr-4 file:h-full file:py-0 file:px-3 file:rounded-lg file:border-0 file:bg-slate-200 file:text-slate-700 hover:file:bg-slate-300 file:text-[11px] file:font-semibold file:normal-case";
    const textareaBaseClass =
      "input w-full p-4 border border-gray-300 rounded-xl focus:ring-0 bg-white text-gray-900 text-sm font-medium disabled:bg-gray-100 disabled:text-gray-500 resize-y";
    const inputBaseClass = isCheckbox
      ? "w-5 h-5 accent-lime-600 cursor-pointer"
      : "input w-full h-10 px-3 border border-gray-300 rounded-xl focus:ring-0 bg-white text-gray-900 text-sm font-medium disabled:bg-gray-100 disabled:text-gray-500";

    const withErrorClass = error ? "border-red-500" : "border-gray-300";
    const buildClassName = (base) => {
      if (overrideInputClassName) {
        const combined = `${className} ${inputClassName}`.trim();
        return combined.length > 0 ? combined : base;
      }
      return `${base} ${withErrorClass} ${className} ${inputClassName}`.trim();
    };

    const renderLabel = () => (
      label && (
        <label
          htmlFor={name}
          className={`${labelColor} ${labelClassName} ${
            isCheckbox ? "mb-0 cursor-pointer" : "mb-2"
          } ${hideLabel ? "mb-0" : ""}`}
        >
          {label} {required && "*"}
        </label>
      )
    );

    return (
      <div
        className={`w-full ${
          isCheckbox ? "flex flex-row items-center gap-2" :
          hideLabel ? "flex items-center" : "flex flex-col"
        }`}
      >
        {!isCheckbox && renderLabel()}

        {type === "select" ? (
          <div className="relative w-full">
            <select
              ref={ref}
              name={name}
              id={name}
              value={value}
              onChange={onChange}
              required={required}
              disabled={disabled}
              aria-describedby={error ? `${name}-error` : undefined}
              className={buildClassName(selectBaseClass)}
              {...props}
            >
              <option value="" disabled>
                {defaultOptionLabel}
              </option>
              {options?.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={Boolean(option.disabled)}
                  className={option.className}
                >
                  {option.label}
                </option>
              ))}
            </select>
            {endIcon && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">
                {endIcon}
              </span>
            )}
          </div>
        ) : type === "file" ? (
          <input
            ref={ref}
            type="file"
            name={name}
            id={name}
            onChange={onChange}
            required={required}
            disabled={disabled}
            accept={accept}
            aria-describedby={error ? `${name}-error` : undefined}
            className={`${buildClassName(fileBaseClass)} h-10`}
            {...props}
          />
        ) : type === "textarea" ? (
          <textarea
            ref={ref}
            name={name}
            id={name}
            value={value}
            onChange={onChange}
            required={required}
            placeholder={placeholder}
            disabled={disabled}
            rows={rows}
            style={style}
            aria-describedby={error ? `${name}-error` : undefined}
            className={buildClassName(textareaBaseClass)}
            {...props}
          />
        ) : (
          <div className={isCheckbox ? "flex items-center" : "relative w-full"}>
            {startIcon && (
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                {startIcon}
              </span>
            )}
            <input
              ref={ref}
              type={type}
              name={name}
              id={name}
              value={value}
              onChange={onChange}
              required={required}
              placeholder={placeholder}
              disabled={disabled}
              aria-describedby={error ? `${name}-error` : undefined}
              className={`${buildClassName(inputBaseClass)} ${
                isCheckbox ? "" : startIcon ? "pl-10" : ""
              }`}
              {...props}
            />
            {endIcon && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">
                {endIcon}
              </span>
            )}
          </div>
        )}

        {isCheckbox && renderLabel()}

        {error && (
          <p id={`${name}-error`} className="text-red-500 text-sm mt-1">
            {error}
          </p>
        )}
      </div>
    );
  }
);

InputField.displayName = "InputField";
